import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import UserIcon from '@mui/icons-material/Group';
import HandshakeIcon from '@mui/icons-material/Handshake';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import LinkIcon from '@mui/icons-material/Link';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import Box from '@mui/material/Box';
import { MenuItemLink, useDataProvider, usePermissions, useSidebarState, useTranslate } from 'react-admin';
import { getLocalStorageId, hasPermission } from './authProvider';
import { ROLE_ADMIN, ROLE_EDIT, ROLE_SALES } from './common_modules/constants/define';
import Badge from '@mui/material/Badge';
import { useEffect, useState } from 'react';
import SubMenu from './common_modules/components/SubMenu';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import DescriptionIcon from '@mui/icons-material/Description';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentsIcon from '@mui/icons-material/Payments';
import CategoryIcon from '@mui/icons-material/Category';
import ApiIcon from '@mui/icons-material/Api';
import { useApiStatus } from './ApiStatusContext';
import { Helmet } from 'react-helmet';
import { useMatch } from 'react-router-dom';
import clsx from 'clsx';
import ShopIcon from '@mui/icons-material/Storefront';

interface CustomMenuProps {
  dense?: boolean;
}

type MenuName = 'menuSettings' | 'menuSales';

const CustomMenu = ({ dense = false }: CustomMenuProps) => {
  const [state, setState] = useState({
    menuSettings: true,
    menuSales: true
  });
  const translate = useTranslate();
  const [open] = useSidebarState();
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const { isApiConnected } = useApiStatus();

  const [newAffiliateCount, setNewAffiliateCount] = useState(0);
  const handleToggle = (menu: MenuName) => {
    setState((state) => ({ ...state, [menu]: !state[menu] }));
  };
  const productRewardSettingsMatch = useMatch('/product-reward-settings/*');

  useEffect(() => {
    if (!hasPermission(permissions, ROLE_EDIT)) {
      return; // 権限がない場合は何も実行しない
    }

    const fetchData = async () => {
      const data = await dataProvider.getList('affiliates', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: { applyStatus: 1 }
      });
      const total = data?.total ?? 0;
      if (total > 0) {
        setNewAffiliateCount(total);
      } else {
        setNewAffiliateCount(0);
      }
    };
    fetchData();
  }, [dataProvider, permissions]);

  return (
    <>
      <Helmet>
        <title>ECサイト管理ダッシュボード</title>
      </Helmet>
      <Box
        sx={{
          // width: open ? 240 : 50,
          width: open ? 240 : 50,
          height: 'calc(100vh - env(safe-area-inset-bottom))',
          marginTop: 1,
          marginBottom: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen
            })
        }}
      >
        {hasPermission(permissions, ROLE_ADMIN) && (
          <MenuItemLink
            to="/accounts"
            primaryText={translate(`resources.accounts.name`)}
            leftIcon={<UserIcon />}
            dense={dense}
          />
        )}
        {hasPermission(permissions, ROLE_EDIT) && isApiConnected && (
          <MenuItemLink
            to="/invitations"
            primaryText={translate(`resources.invitations.name`)}
            leftIcon={<MailOutlineIcon />}
            dense={dense}
          />
        )}
        {hasPermission(permissions, ROLE_EDIT) && isApiConnected && (
          <MenuItemLink
            to="/affiliates"
            primaryText={translate(`resources.affiliates.name`)}
            leftIcon={<HandshakeIcon />}
            dense={dense}
          >
            {newAffiliateCount > 0 && (
              <Badge
                badgeContent={newAffiliateCount}
                color="primary"
                sx={{ '& .MuiBadge-badge': { right: '-8px', top: '20%' } }}
              >
                {translate(`resources.affiliates.name`)}
              </Badge>
            )}
          </MenuItemLink>
        )}
        {hasPermission(permissions, ROLE_SALES) && (
          <MenuItemLink
            to="/orders"
            primaryText={translate(`resources.orders.name`)}
            leftIcon={<ShoppingCartIcon />}
            dense={dense}
          />
        )}
        {hasPermission(permissions, ROLE_SALES) && (
          <MenuItemLink
            to="/buyers"
            primaryText={translate(`resources.buyers.name`)}
            leftIcon={<PersonIcon />}
            dense={dense}
          />
        )}
        <MenuItemLink
          to="/sales"
          primaryText={translate(`resources.sales.name.sales`)}
          leftIcon={<LeaderboardIcon />}
          dense={dense}
        />
        {hasPermission(permissions, ROLE_SALES) && (
          <MenuItemLink
            to="/invoices"
            primaryText={translate(`resources.invoices.name`)}
            leftIcon={<PaymentsIcon />}
            dense={dense}
          />
        )}
        {hasPermission(permissions, ROLE_SALES) && (
          <MenuItemLink
            to="/partner-payments"
            primaryText={translate(`resources.partner-payments.name`)}
            leftIcon={<PaymentsIcon />}
            dense={dense}
          />
        )}
        {hasPermission(permissions, ROLE_ADMIN) && (
          <SubMenu
            handleToggle={() => handleToggle('menuSettings')}
            isOpen={state.menuSettings}
            name="resources.settings.name"
            icon={<SettingsIcon />}
            dense={dense}
          >
            <MenuItemLink
              to={`/settings/${getLocalStorageId()}`}
              primaryText={translate(`resources.settings.name`)}
              leftIcon={<SettingsIcon />}
              dense={dense}
              className={clsx({
                'RaMenuItemLink-active': productRewardSettingsMatch
              })}
            />
            {/* <MenuItemLink
              to={`/terms-settings/${getLocalStorageId()}`}
              primaryText={translate(`resources.terms-settings.name`)}
              leftIcon={<DescriptionIcon />}
              dense={dense}
            /> */}
            <MenuItemLink
              to="/client-pr/me"
              primaryText={translate(`resources.client-pr.name`)}
              leftIcon={<ShopIcon />}
              dense={dense}
            />
            <MenuItemLink
              to={`/contract-settings/${getLocalStorageId()}`}
              primaryText={translate(`resources.contract-settings.name`)}
              leftIcon={<DescriptionIcon />}
              dense={dense}
            />

            <MenuItemLink
              to={`/api-settings`}
              primaryText={translate(`resources.script-settings.name`)}
              leftIcon={<ApiIcon />}
              dense={dense}
            />
          </SubMenu>
        )}
        {hasPermission(permissions, ROLE_EDIT) && isApiConnected && (
          <MenuItemLink
            to={`/invite-url/${getLocalStorageId()}/show`}
            primaryText={translate(`resources.settings.fields.inviteCodeUrl`)}
            leftIcon={<LinkIcon />}
            dense={dense}
          />
        )}
        {/* EDITは表示はできるが変更できず、表示する必要性がないのでADMINのみ */}
        {hasPermission(permissions, ROLE_ADMIN) && (
          <MenuItemLink
            to={`/client-url/${getLocalStorageId()}/show`}
            primaryText={translate(`resources.settings.fields.ecSiteUrl`)}
            leftIcon={<LinkIcon />}
            dense={dense}
          />
        )}
        {hasPermission(permissions, ROLE_ADMIN) && (
          <MenuItemLink to={`/contracts`} primaryText={`利用規約`} leftIcon={<DescriptionIcon />} dense={dense} />
        )}
      </Box>
    </>
  );
};

export default CustomMenu;
